import type { DTOBlockingFriendsResult } from 'src/apis/dto/DTOBlockingFriendsResult';
import type { DTOBlockingInfo } from 'src/apis/dto/DTOBlockingInfo';
import type { DTOBlockingInfoRequest } from 'src/apis/dto/DTOBlockingInfoRequest';
import type { DTOBlockingInfoResponse } from 'src/apis/dto/DTOBlockingInfoResponse';
import type { DTOUnblockResponse } from 'src/apis/dto/DTOUnblockResponse';
import type { DTOUnblockResponseV3 } from 'src/apis/dto/DTOUnblockResponseV3';

import { APIClientType } from '.';

/**
 * 사용자 차단 관리 서비스.
 * 친구 또는 매치유저를 차단하고 차단목록을 관리하는 기능을 제공한다. 현재 차단의 효과는 친구의 경우 친구목록에서 사라지고 API상의 친구목록에서도
 * 사라지게 된다. 향후에 랜덤매치 시에도 차단한 사용자는 제외시킬 예정이다.
 *
 * @since 2.5
 */
export interface BlockingServiceType {
  /**
   * 친구를 차단한다.
   *
   * @param friendId 친구 ID
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   * @throws IllegalArgumentException 친구 ID가 잘못된 경우
   * @throws NotFriendException 친구 관계가 아닌 경우. APi버전 173 이상에서만 발생. 친구가 탈퇴한 경우에도 발생할 수 있으므로
   * 이 에러가 발생하면 친구 목록을 갱신하여야 함
   */
  blockFriend: (friendId: string) => Promise<void>;

  /**
   * 친구를 차단한다.
   *
   * @param friendIds 친구 ID 목록
   * @return BlockingFriendsResult 친구 차단 결과 (차단 성공한 friends id와 실패한 friends id를 포함한다)
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   */
  blockFriends: (friendIds: string[]) => Promise<DTOBlockingFriendsResult>;

  /**
   * @deprecated
   *
   * 사용자 차단과 봇 차단 관심사가 섞여 있어, 이를 분리합니다.
   * 아래 대체 API를 조합하여 사용해주세요.
   *
   * 사용자 차단 목록 조회 - blockingInfos API
   * Bot 차단 목록 조회 - listBotBlockings API
   * 사용자 차단 해제 - unblock2 API
   * Bot 차단 해제 - unblockBot API
   *
   * 차단 목록을 가져온다. 임의차단한 사용자만 가져오며 신고차단한 사용자는 표시되지 않는다.
   *
   * @return 차단 목록.
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   */
  listBlockings: () => Promise<DTOBlockingInfo[]>;

  /**
   * 커서 기반으로 사용자 차단 목록을 조회한다.
   *
   * [Guide]
   * Data set : 차단 시간을 기준으로 ASC
   * direction : Left <=> Right
   * size : (1,2)
   * nextCursor : cursor
   * P : BlockingInfoResponse's previousCursor
   * N : BlockingInfoResponse's nextCursor
   *
   * 아래는 Data Search 예시입니다.
   * Left와 Right 사이에 있는 숫자가 낮을 수록 상대적으로 과거에 차단한 데이터를 의미합니다.
   *
   *         P N
   *         | |
   * Left | (1,2),(3,4),(5,6),(7,8),(9,10) | Right
   *
   * Ex1. 차단 시간을 기준으로 오래된 순서로 조회 (임의 차단 유형 만 조회 - type=AT_WILL)
   *
   *  Left | 1,2,3,4 | Right
   *  처음 조회를 수행시, nextCursor는 null을 전달한다.
   *  => request (direction=FORWARD, nextCursor=null, type=AT_WILL, size=2)
   *  => response (previousCursor=A, blockingInfos=[1,2], nextCursor=B)
   *
   *          ==>
   *          P N
   *          | |
   *  Left | (1,2),(3,4) | Right
   *  응답 받은 nextCursor != null, 아직 조회 할 수 있는 데이터가 더 남아 있다.
   *  사용자가 스크롤을 내리면 응답받은 nextCursor를 가지고 재요청한다.
   *  => request (direction=FORWARD, nextCursor=B, type=AT_WILL, size=2)
   *  => response (previousCursor=C, blockingInfos=[3,4], nextCursor=D)
   *
   *                ==>
   *                P N
   *                | |
   *  Left | (1,2),(3,4) | Right
   *  응답 받은 nextCursor != null, 아직 조회 할 수 있는 데이터가 더 남아 있다.
   *  사용자가 스크롤을 내리면 응답받은 nextCursor를 가지고 재요청한다.
   *  => request (direction=FORWARD, nextCursor=D, type=AT_WILL, size=2)
   *  => response (previousCursor=null, blockingInfos=[], nextCursor=null)
   *
   *                         ==>
   *                         P N
   *                         | |
   *  Left | (1,2),(3,4) | Right
   *  응답 받은 nextCursor == null, 더 이상 조회할 수 있는 데이터가 없다.
   *
   *
   * Ex2. 차단 시간을 기준으로 최신 순서로 조회 (차단 유형 구분 없이 조회 - type=null)
   *
   *  Left | 1,2,3 | Right
   *  => request (direction=BACKWARD, nextCursor=null, type=null, size=2)
   *  => response (previousCursor=A, blockingInfos=[3,2], nextCursor=B)
   *
   *              <==
   *              N P
   *              | |
   *  Left | (1),(2,3) | Right
   *  => request (direction=BACKWARD, nextCursor=B, type=null, size=2)
   *  => response (previousCursor=C, blockingInfos=[1], nextCursor=null)
   *
   *     <==
   *  N       P
   *  |       |
   *  Left | (1),(2,3) | Right
   *  응답 받은 nextCursor == null, 더 이상 조회할 수 있는 데이터가 없다.
   *
   *
   * 아래는 Client에서 모든 차단 목록을 메모리에 들고 있지 않을 수 있는, Client OOM을 방지하고 메모리 최적화를 할 수 있는 응용 방식입니다.
   * Ex3. 차단 시간을 기준으로 오래된 순서로 조회 (임의 차단 유형 만 조회 - type=AT_WILL), 사용자가 스크롤을 내리다가 위로 올린다.
   *
   *  Left | 1,2,3,4,5,6 | Right
   *  처음 조회를 수행시, nextCursor는 null을 전달한다.
   *  => request (direction=FORWARD, nextCursor=null, type=AT_WILL, size=2)
   *  => response (previousCursor=A, blockingInfos=[1,2], nextCursor=B)
   *
   *          ==>
   *          P N
   *          | |
   *  Left | (1,2),(3,4),(5,6) | Right
   *  응답 받은 nextCursor != null, 아직 조회 할 수 있는 데이터가 더 남아 있다.
   *  사용자가 스크롤을 내리면 응답받은 nextCursor를 가지고 재요청한다.
   *  => request (direction=FORWARD, nextCursor=B, type=AT_WILL, size=2)
   *  => response (previousCursor=C, blockingInfos=[3,4], nextCursor=D)
   *
   *                ==>
   *                P N
   *                | |
   *  Left | (1,2),(3,4),(5,6) | Right
   *  응답 받은 nextCursor != null, 아직 조회 할 수 있는 데이터가 더 남아 있다.
   *  사용자가 스크롤을 내리면 응답받은 nextCursor를 가지고 재요청한다.
   *  => request (direction=FORWARD, nextCursor=D, type=AT_WILL, size=2)
   *  => response (previousCursor=E, blockingInfos=[5,6], nextCursor=F)
   *
   *                      ==>
   *                      P N
   *                      | |
   *  Left | (1,2),(3,4),(5,6) | Right
   *  사용자가 스크롤을 위로 올린다. 방향 전환이 필요하다.
   *  앞으로는 direction을 BACKWARD를 사용한다.
   *  방향 전환을 위해, 응답 받은 previousCursor를 다음 요청의 nextCursor로 사용.
   *  => request (direction=BACKWARD, nextCursor=E, type=AT_WILL, size=2)
   *  => response (previousCursor=D, blockingInfos=[4,3], nextCursor=C)
   *
   *                <==
   *                N P
   *                | |
   *  Left | (1,2),(3,4),(5,6) | Right
   *  응답 받은 nextCursor != null, 아직 조회 할 수 있는 데이터가 더 남아 있다.
   *  사용자가 스크롤을 올리면 응답받은 nextCursor를 가지고 재요청한다.
   *  => request (direction=BACKWARD, nextCursor=C, type=AT_WILL, size=2)
   *  => response (previousCursor=B, blockingInfos=[2,1], nextCursor=A)
   *
   *          <==
   *          N P
   *          | |
   *  Left | (1,2),(3,4),(5,6) | Right
   *  응답 받은 nextCursor != null, 아직 조회 할 수 있는 데이터가 더 남아 있다.
   *  사용자가 스크롤을 올리면 응답받은 nextCursor를 가지고 재요청한다.
   *  => request (direction=BACKWARD, nextCursor=A, type=AT_WILL, size=2)
   *  => response (previousCursor=null, blockingInfos=[], nextCursor=null)
   *
   *  <==
   *  N P
   *  | |
   *  Left | (1,2),(3,4),(5,6) | Right
   *  응답 받은 nextCursor == null, 더 이상 조회할 수 있는 데이터가 없다.
   *
   * @param request 커서 기반으로 사용자 차단 목록을 조회하는 요청
   * @return 커서 기반의 사용자 차단 목록.
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   * @throws IllegalArgumentException 사용자 차단 목록을 조회하는 요청이 잘못된 경우
   */
  blockingInfos: (request: DTOBlockingInfoRequest) => Promise<DTOBlockingInfoResponse>;

  /**
   * @deprecated
   * 차단을 해제한다.
   *
   * @param blockingId 차단 ID
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   * @throws IllegalArgumentException 차단 ID가 잘못된 경우
   */
  unblock: (blockingId: string) => Promise<void>;

  /**
   * @deprecated
   * 차단을 해제한다.
   *
   * 대체 API를 사용해주세요.
   * Bot 차단 해제 - unblockBot API
   *
   * @param blockingId 차단 ID
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   * @throws IllegalArgumentException 차단 ID가 잘못된 경우
   * @return UnblockResponse which contains a friend id if unblocked user is a friend
   */
  unblock2: (blockingId: string) => Promise<DTOUnblockResponse>;

  /**
   * 차단을 해제한다.
   *
   * @param blockingId 차단 ID
   * @throws AuthenticationException 로그인이 안 된 상태인 경우
   * @throws IllegalArgumentException 차단 ID가 잘못된 경우
   * @return UnblockResponseV3 which contains a friend id if unblocked user is a friend
   */
  unblockV3: (blockingId: string) => Promise<DTOUnblockResponseV3>;
}

export class BlockingService implements BlockingServiceType {
  private client: APIClientType;

  constructor(customClient: APIClientType) {
    this.client = customClient;
  }

  public blockFriend = async (friendId: string) => {
    return this.client.call<void, Parameters<typeof this.blockFriend>>({
      method: 'blockFriend',
      data: [friendId],
    });
  };

  public blockFriends = async (friendIds: string[]) => {
    return this.client.call<DTOBlockingFriendsResult, Parameters<typeof this.blockFriends>>({
      method: 'blockFriends',
      data: [friendIds],
    });
  };

  public listBlockings = async () => {
    return this.client.call<DTOBlockingInfo[], void>({
      method: 'listBlockings',
    });
  };

  public blockingInfos = async (request: DTOBlockingInfoRequest) => {
    return this.client.call<DTOBlockingInfoResponse, Parameters<typeof this.blockingInfos>>({
      method: 'blockingInfos',
      data: [request],
    });
  };

  public unblock = async (blockingId: string) => {
    return this.client.call<void, Parameters<typeof this.unblock>>({
      method: 'unblock',
      data: [blockingId],
    });
  };

  public unblock2 = async (blockingId: string) => {
    return this.client.call<DTOUnblockResponse, Parameters<typeof this.unblock2>>({
      method: 'unblock2',
      data: [blockingId],
    });
  };

  public unblockV3 = async (blockingId: string) => {
    return this.client.call<DTOUnblockResponseV3, Parameters<typeof this.unblockV3>>({
      method: 'unblockV3',
      data: [blockingId],
    });
  };
}
